import React, { useEffect } from 'react';
import { useStore } from 'zustand';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useMapStore } from 'stores/map/mapStore';
import { stylesSelect } from 'utils';
import {
  GOOGLE_MAPS_API,
  MAP_PREFIX,
  MAP_RADIAL_OPTIONS,
} from 'utils/constants';
import { MapPlusSidebar } from './component/map-plus-sidebar';
import InputSelect from '../select/InputSelect';
import { PlaceAutocompleteCustom } from './component/autocomplete-custom';
import IconComponent from '../FlashCards/Icon';
import { SelectedPlace } from './type';
import {
  ModelOptions,
  ProductOption,
  retailerStore,
} from '@/stores/map/retailProductsStore';

const MapComponentStores = () => {
  const API_KEY = process.env.API_KEY_GOOGLEMAPS as string;
  const {
    distanceOptions,
    clientLocation,
    getSelectedDistance,
    setSelectedDistance,
    hasSetLocation,
    setSelected,
    hasLoaded,
  } = useStore(useMapStore, (state) => ({
    getSelectedDistance: state.getSelectedDistance,
    setSelectedDistance: state.setSelectedDistance,
    distanceOptions: state.distanceOptions,
    clientLocation: state.clientLocation,
    setClientLocation: state.setClientLocation,
    setSelectedKey: state.setSelectedKey,
    hasSetLocation: state.hasSetLocation,
    setSelected: state.setSelected,
    hasLoaded: state.hasLoaded,
  }));

  const {
    _hasHydrated,
    productOption,
    modelOption,
    retailerProducts,
    getFeedData,
    getProductOptions,
    setSelectedProduct,
    setSelectedModel,
    setRetailerProducts,
  } = retailerStore();

  const reverseGeocode = async (lat: number, lng: number) => {
    const url = `${GOOGLE_MAPS_API}${MAP_PREFIX}?latlng=${lat},${lng}&key=${API_KEY}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK') {
        const result = data.results[0],
          selected: SelectedPlace = {
            address: result.formatted_address ?? '',
            geometry: { lat, lng },
          };

        setSelected(selected);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGeolocationLookupClick = async () => {
    hasSetLocation() &&
      clientLocation.location &&
      reverseGeocode(clientLocation.location.lat, clientLocation.location.lng);
  };

  const handleProductChange = (selected: ProductOption) => {
    if (!selected) {
      setSelectedProduct(null);
      setSelectedModel(null);
      return;
    }

    setSelectedProduct(selected);
    setSelectedModel(null);
  };

  const handleModelChange = async (selected: ModelOptions) => {
    if (!selected) {
      setSelectedModel(null);
      return;
    }

    setSelectedModel(selected);
    getFeedData();
  };

  useEffect(() => {
    if (_hasHydrated) {
      getProductOptions();
    }
  }, [_hasHydrated]);

  return (
    <APIProvider apiKey={API_KEY} libraries={['places', 'geometry', 'marker']}>
      <div className='w-full'>
        <div className='flex flex-row items-center gap-3 p-2 '>
          <div className='flex w-full basis-4/12 rounded-full shadow-sm'>
            <span className='inline-flex min-w-fit items-center rounded-s-full border border-e-0 pl-4 pr-2 text-sm'>
              {hasLoaded.initialLoad ? (
                <div
                  className='inline-block h-4 w-4 animate-spin rounded-full border-[3px] border-current border-t-transparent text-gray-500'
                  role='status'
                  aria-label='loading'
                >
                  <span className='sr-only'>Loading...</span>
                </div>
              ) : (
                <IconComponent
                  name='MdSearch'
                  className={`h-5 w-5 text-gray-500`}
                />
              )}
            </span>
            <div className='relative flex w-full items-center'>
              <PlaceAutocompleteCustom />
            </div>

            <div className='hs-tooltip inline-block'>
              <button
                type='button'
                className={`hs-tooltip-toggle inline-flex h-[3rem] w-[2.875rem] flex-shrink-0 items-center justify-center gap-x-2 rounded-e-full border border-s-0 text-sm font-semibold disabled:pointer-events-none disabled:opacity-50 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-primary`}
                onClick={handleGeolocationLookupClick}
              >
                <IconComponent
                  name='PiGpsFixFill'
                  className='h-5 w-5 text-blue-500'
                />
              </button>
            </div>
          </div>

          <div className='flex w-full basis-2/12 items-center gap-3 sm:max-w-xs'>
            <span className='w-full max-w-fit'>Radius :</span>
            <InputSelect
              customStyles={stylesSelect}
              value={distanceOptions.selected ? getSelectedDistance() : null}
              onChange={setSelectedDistance}
              error=''
              className='text-gray-5 text-sm font-normal'
              classNamePrefix=''
              formatOptionLabel={''}
              instanceId='distance'
              isMulti={false}
              isDisabled={false}
              placeholder=''
              options={MAP_RADIAL_OPTIONS}
              icon=''
              isClearable
            />
          </div>
          <div className='flex w-full basis-3/12 items-center gap-3 sm:max-w-xs'>
            <span className='w-full max-w-fit'>Product:</span>
            <InputSelect
              customStyles={stylesSelect}
              value={productOption}
              onChange={handleProductChange}
              error=''
              className='text-gray-5 w-full text-sm font-normal'
              classNamePrefix=''
              formatOptionLabel={''}
              instanceId='product'
              isMulti={false}
              isDisabled={false}
              placeholder=''
              options={getProductOptions()}
              icon=''
              isClearable
              autoFocus={false}
            />
          </div>

          <div className='flex w-full basis-3/12 items-center gap-3 sm:max-w-xs'>
            <span className='w-full max-w-fit'>Model:</span>
            <InputSelect
              customStyles={stylesSelect}
              value={modelOption}
              onChange={handleModelChange}
              error=''
              className='text-gray-5 w-full text-sm font-normal'
              classNamePrefix=''
              formatOptionLabel={''}
              instanceId='model'
              isMulti={false}
              isDisabled={false}
              placeholder=''
              options={productOption && productOption.models}
              icon=''
              isClearable
              autoFocus={false}
            />
          </div>
        </div>

        <div className='overflow-auto'>
          <MapPlusSidebar isModal='true' />
        </div>
      </div>
    </APIProvider>
  );
};

export default MapComponentStores;
